import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AppDialog from '../AppDialog';

const NAME_FIELD = 'name';
const SECTION_FIELD = 'section';
const STORAGE_SIZE_FIELD = 'storageSize';
const PRICE_FIELD = 'price';
const FACILITY_FIELD = 'facility';
const STORAGE_TYPE_FIELD = 'storageType';
const CLIMATE_CONTROLLED_FIELD = 'climateControlled';

export default function EditStorageUnitModal(props) {
    const { open, storageUnit, setStorageUnit, handleSave, facilities, storageTypes, sections, close } = props;

    const [errors, setErrors] = useState({});

    /**
     * Selects are controlled, so only hand them a value that matches one of the loaded options.
     * Anything else (a not-yet-loaded list, or the 0 on a brand new unit) has to render as empty,
     * otherwise MUI drops the Select back to uncontrolled and selections stop sticking.
     * @param options {Array<{id: number}>}
     * @param selectedId {number=}
     * @return {number|string}
     */
    function selectValue(options, selectedId) {
        return options.some((option) => option.id === selectedId) ? selectedId : '';
    }

    function setStorageUnitField(event, fieldName) {
        const copyStorageUnit = {...storageUnit};

        switch (fieldName) {
        case NAME_FIELD:
            copyStorageUnit.name = event.target.value;
            break;
        case SECTION_FIELD:
            copyStorageUnit.sectionId = event.target.value;
            break;
        case STORAGE_SIZE_FIELD:
            copyStorageUnit.storageSize = event.target.value;
            break;
        case PRICE_FIELD:
            copyStorageUnit.price = event.target.value === '' ? '' : Number(event.target.value);
            break;
        case FACILITY_FIELD:
            copyStorageUnit.facilityId = event.target.value;
            break;
        case STORAGE_TYPE_FIELD:
            copyStorageUnit.storageUnitTypeId = event.target.value;
            break;
        case CLIMATE_CONTROLLED_FIELD:
            copyStorageUnit.climateControlled = event.target.checked;
            break;
        default:
            return;
        }

        setStorageUnit(copyStorageUnit);
    }

    /**
     * @return {Object<string, string>} field name -> message, empty when the unit is valid
     */
    function validate() {
        const found = {};

        if(!storageUnit?.name?.trim()) {
            found.name = 'Name is required';
        }

        if(!storageUnit?.storageSize?.trim()) {
            found.storageSize = 'Storage size is required';
        }

        if(storageUnit?.price === '' || storageUnit?.price === null || storageUnit?.price === undefined) {
            found.price = 'Price is required';
        } else if(Number(storageUnit.price) < 0) {
            found.price = 'Price cannot be negative';
        }

        if(!sections.some((section) => section.id === storageUnit?.sectionId)) {
            found.sectionId = 'Section is required';
        }

        if(!facilities.some((facility) => facility.id === storageUnit?.facilityId)) {
            found.facilityId = 'Facility is required';
        }

        if(!storageTypes.some((type) => type.id === storageUnit?.storageUnitTypeId)) {
            found.storageUnitTypeId = 'Storage unit type is required';
        }

        return found;
    }

    function handleSubmit() {
        const found = validate();
        setErrors(found);

        if(Object.keys(found).length === 0) {
            handleSave();
        }
    }

    // don't show last session's validation errors when the dialog is reopened
    useEffect(() => {
        if(open) {
            setErrors({});
        }
    }, [open]);

    return(
        <AppDialog
            open={open}
            onClose={close}
            title={storageUnit?.id ? `Edit Storage Unit ${storageUnit?.name}` : 'Add New Storage Unit'}
            onSubmit={handleSubmit}
            submitText='Save'
        >
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label='Name'
                        value={storageUnit?.name ?? ''}
                        error={!!errors.name}
                        helperText={errors.name}
                        onChange={(event) => setStorageUnitField(event, NAME_FIELD)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.sectionId}>
                        <InputLabel id='storage-unit-section-label'>Section</InputLabel>
                        <Select
                            labelId='storage-unit-section-label'
                            label='Section'
                            value={selectValue(sections, storageUnit?.sectionId)}
                            onChange={(event) => setStorageUnitField(event, SECTION_FIELD)}
                        >
                            {sections.map((section) =>
                                <MenuItem key={section.id} value={section.id}>{section.name}</MenuItem>
                            )}
                        </Select>
                        {errors.sectionId && <FormHelperText>{errors.sectionId}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        label='Storage Size'
                        value={storageUnit?.storageSize ?? ''}
                        error={!!errors.storageSize}
                        helperText={errors.storageSize}
                        onChange={(event) => setStorageUnitField(event, STORAGE_SIZE_FIELD)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        type='number'
                        label='Price'
                        value={storageUnit?.price ?? ''}
                        error={!!errors.price}
                        helperText={errors.price}
                        InputProps={{
                            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: '0.01' }}
                        onChange={(event) => setStorageUnitField(event, PRICE_FIELD)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.facilityId}>
                        <InputLabel id='storage-unit-facility-label'>Facility</InputLabel>
                        <Select
                            labelId='storage-unit-facility-label'
                            label='Facility'
                            value={selectValue(facilities, storageUnit?.facilityId)}
                            onChange={(event) => setStorageUnitField(event, FACILITY_FIELD)}
                        >
                            {facilities.map((facility) =>
                                <MenuItem key={facility.id} value={facility.id}>{facility.name}</MenuItem>
                            )}
                        </Select>
                        {errors.facilityId && <FormHelperText>{errors.facilityId}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.storageUnitTypeId}>
                        <InputLabel id='storage-unit-type-label'>Storage Unit Type</InputLabel>
                        <Select
                            labelId='storage-unit-type-label'
                            label='Storage Unit Type'
                            value={selectValue(storageTypes, storageUnit?.storageUnitTypeId)}
                            onChange={(event) => setStorageUnitField(event, STORAGE_TYPE_FIELD)}
                        >
                            {storageTypes.map((type) =>
                                <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                            )}
                        </Select>
                        {errors.storageUnitTypeId && <FormHelperText>{errors.storageUnitTypeId}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        label='Climate Controlled'
                        control={
                            <Checkbox
                                checked={storageUnit?.climateControlled ?? false}
                                onChange={(event) => setStorageUnitField(event, CLIMATE_CONTROLLED_FIELD)}
                            />
                        }
                    />
                </Grid>
            </Grid>
        </AppDialog>
    );
}

/**
 * These arrive as plain JSON from the API (and a spread copy is a plain object either way),
 * so they are described by shape rather than by the DTO class.
 */
const lookupShape = PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
});

EditStorageUnitModal.propTypes = {
    open: PropTypes.bool.isRequired,
    storageUnit: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        sectionId: PropTypes.number,
        storageSize: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        facilityId: PropTypes.number,
        storageUnitTypeId: PropTypes.number,
        climateControlled: PropTypes.bool,
    }),
    handleSave: PropTypes.func.isRequired,
    setStorageUnit: PropTypes.func.isRequired,
    facilities: PropTypes.arrayOf(lookupShape).isRequired,
    sections: PropTypes.arrayOf(lookupShape).isRequired,
    storageTypes: PropTypes.arrayOf(lookupShape).isRequired,
    close: PropTypes.func.isRequired,
};